import { Fragment, useCallback } from "react";
import { useTranslation } from "react-i18next";

import { Gradients } from "components/gradients";
import { useAnimatedValue } from "hooks/use-animated-value";
import { useGauge } from "hooks/use-gauge";
import { useStore } from "hooks/use-store";
import { PLACEHOLDER } from "utils/constants";
import { Color } from "utils/theme";
import { opacity } from "utils/canvas";
import {
  DEFAULT_TICK_COLOR,
  MAIN_ARC_BG,
  MAIN_ARC_STROKE,
  OUTER_ARC_BG,
  OUTER_ARC_STROKE,
} from "components/gauge-style";
import { SvgGradients } from "types/enums";

// Constants
const START_ANGLE = 30;
const END_ANGLE = 330;
const MAX_VALUE = 60;
const DIAMETER = 450;
const ARC_WIDTH = 22;
const TICK_OFFSET = ARC_WIDTH + 10;
const TICK_WIDTH = 4;
const TICK_LENGTH = 15;
const STROKE_CAP = "round";
const MIN_SOG_TO_SHOW = 1;

export const SafetyGauge = () => {
  const { t } = useTranslation();

  // Store values
  const config = useStore((state) => state.config);
  const sog = useStore((state) => state.sog);
  const safetySpeed = useStore((state) => state.rog);

  // Animated values for smooth transitions
  const animatedSog = useAnimatedValue(sog);
  const animatedSafetySpeed = useAnimatedValue(safetySpeed);

  // Gauge setup
  const gauge = useGauge({
    domain: [0, MAX_VALUE],
    startAngle: START_ANGLE,
    endAngle: END_ANGLE,
    numTicks: MAX_VALUE,
    diameter: DIAMETER,
  });

  // A safety speed of 0 means no safety limit is set, so never warn
  const hasSafetySpeed = safetySpeed !== null && safetySpeed > 0;
  const isAboveSafetySpeed =
    sog !== null && hasSafetySpeed && sog >= safetySpeed;

  // Label for optimized speed (comfort or safety)
  const optimizedSpeedLabel = config?.company_settings?.has_comfort_speed
    ? t("dashboard.comfort_speed")
    : t("dashboard.safety_speed");

  // Determine tick color based on current SOG
  const getTickColor = useCallback(
    (tickValue: number, defaultColor: string) => {
      const tickInSogRange = sog !== null && tickValue <= sog;
      const tickAboveSafetySpeed = hasSafetySpeed && tickValue >= safetySpeed;

      // Above safety speed and in SOG range
      if (tickAboveSafetySpeed && tickInSogRange && isAboveSafetySpeed) {
        return Color.Rose[600];
      }

      // Above safety speed
      if (tickAboveSafetySpeed) return opacity(Color.Rose[500], 0.5);

      // In SOG range
      if (tickInSogRange) return Color.Indigo[400];

      return defaultColor;
    },
    [safetySpeed, sog]
  );

  return (
    <svg
      {...gauge.getSVGProps(ARC_WIDTH * 1.5)}
      preserveAspectRatio="xMidYMid meet"
      className="w-full h-full"
    >
      {/* Defs */}
      <Gradients />

      {/* Main arc */}
      <g id="main-arc">
        <path
          id="main-arc-stroke"
          {...gauge.getArcProps({
            offset: 0,
            startAngle: START_ANGLE,
            endAngle: END_ANGLE,
          })}
          fill="none"
          stroke={MAIN_ARC_STROKE}
          strokeWidth={ARC_WIDTH * 1.4}
          strokeLinecap={STROKE_CAP}
        />
        <path
          id="main-arc-bg"
          {...gauge.getArcProps({
            offset: 0,
            startAngle: START_ANGLE,
            endAngle: END_ANGLE,
          })}
          fill="none"
          stroke={MAIN_ARC_BG}
          strokeWidth={ARC_WIDTH * 1.3}
          strokeLinecap={STROKE_CAP}
        />
        <path
          id="main-arc-value"
          {...gauge.getArcProps({
            offset: 0,
            startAngle: START_ANGLE,
            endAngle:
              animatedSog > MAX_VALUE
                ? gauge.valueToAngle(MAX_VALUE)
                : gauge.valueToAngle(
                    animatedSog > MIN_SOG_TO_SHOW ? animatedSog : 0
                  ),
          })}
          fill="none"
          stroke={
            isAboveSafetySpeed
              ? `url(#${SvgGradients.Red})`
              : `url(#${SvgGradients.Blue})`
          }
          strokeWidth={sog !== null && sog > 0 ? ARC_WIDTH : 0}
          strokeLinecap={STROKE_CAP}
        />
      </g>

      {/* Outer arc */}
      <g id="outer-arc">
        <path
          id="outer-arc-stroke"
          {...gauge.getArcProps({
            offset: ARC_WIDTH * 1.2,
            startAngle: START_ANGLE,
            endAngle: END_ANGLE,
          })}
          fill="none"
          stroke={OUTER_ARC_STROKE}
          strokeWidth={ARC_WIDTH * 0.55}
          strokeLinecap={STROKE_CAP}
        />
        <path
          id="outer-arc-bg"
          {...gauge.getArcProps({
            offset: ARC_WIDTH * 1.2,
            startAngle: START_ANGLE,
            endAngle: END_ANGLE,
          })}
          fill="none"
          stroke={OUTER_ARC_BG}
          strokeWidth={ARC_WIDTH * 0.5}
          strokeLinecap={STROKE_CAP}
        />
        {hasSafetySpeed && (
          <path
            id="eouter-arc-value-1"
            {...gauge.getArcProps({
              offset: ARC_WIDTH * 1.2,
              startAngle: gauge.valueToAngle(
                animatedSafetySpeed > MAX_VALUE
                  ? MAX_VALUE
                  : animatedSafetySpeed
              ),
              endAngle: END_ANGLE,
            })}
            fill="none"
            stroke={
              isAboveSafetySpeed
                ? `url(#${SvgGradients.Red})`
                : `url(#${SvgGradients.RedFaded})`
            }
            opacity={1}
            strokeWidth={ARC_WIDTH * 0.5}
            strokeLinecap={STROKE_CAP}
          />
        )}
      </g>

      {/* Ticks */}
      <g id="ticks">
        {gauge.ticks.map((angle, index) => {
          const asValue = gauge.angleToValue(angle);
          const showText = asValue % 5 === 0;
          const tickColor = getTickColor(asValue, DEFAULT_TICK_COLOR);

          return (
            <Fragment key={`tick-${angle}-${index}`}>
              <line
                strokeWidth={TICK_WIDTH}
                stroke={tickColor}
                strokeLinecap={"round"}
                {...gauge.getTickProps({
                  angle,
                  length: showText ? TICK_LENGTH : TICK_LENGTH * 0.5,
                  offset: -TICK_OFFSET,
                })}
              />
              {showText && (
                <text
                  className="text-base font-medium font-mono"
                  fill={tickColor}
                  {...gauge.getLabelProps({ angle, offset: TICK_OFFSET * 1.9 })}
                >
                  {asValue}
                </text>
              )}
            </Fragment>
          );
        })}
      </g>

      {/* Center value and label */}
      <g id="center">
        <text
          y={"15%"}
          className="text-xl font-semibold uppercase"
          fill={isAboveSafetySpeed ? Color.Rose[600] : Color.Primary[500]}
          alignmentBaseline="middle"
          dominantBaseline="middle"
          textAnchor="middle"
        >
          {t("dashboard.sog")}
        </text>
        <text
          fill={isAboveSafetySpeed ? Color.Rose[600] : Color.White}
          className="text-9xl font-bold font-mono"
          alignmentBaseline="middle"
          dominantBaseline="middle"
          textAnchor="middle"
        >
          {sog !== null ? Math.round(sog) : PLACEHOLDER}
        </text>
      </g>

      {/* Center bottom label and value */}
      <g id="center-bottom">
        <text
          y={"45%"}
          className="text-lg font-semibold"
          fill={isAboveSafetySpeed ? Color.Rose[600] : Color.Primary[500]}
          alignmentBaseline="baseline"
          dominantBaseline="baseline"
          textAnchor="middle"
        >
          {optimizedSpeedLabel}
        </text>
        <text
          y={"38%"}
          fill={isAboveSafetySpeed ? Color.Rose[600] : Color.White}
          className="text-7xl font-semibold font-mono"
          alignmentBaseline="baseline"
          dominantBaseline="baseline"
          textAnchor="middle"
        >
          {safetySpeed !== null ? Math.round(safetySpeed) : PLACEHOLDER}
        </text>
      </g>
    </svg>
  );
};
